import React, { useMemo } from "react";
import { CalendarClock, Rocket, Wallet, TrendingDown, Settings2 } from "lucide-react";
import { Button } from "../../atoms";

export interface StartupSetupHeroProps {
  startupName?: string;
  hasStartDate: boolean;
  hasInitialFunding: boolean;
  expenseCount: number;
  onSetTimeline: () => void;
  onAddFunding: () => void;
  onAddExpense: () => void;
  onOpenSettings: () => void;
}

export const StartupSetupHero: React.FC<StartupSetupHeroProps> = ({
  startupName,
  hasStartDate,
  hasInitialFunding,
  expenseCount,
  onSetTimeline,
  onAddFunding,
  onAddExpense,
  onOpenSettings,
}) => {
  const steps = useMemo(
    () => [
      {
        id: "timeline",
        title: "Set your startup timeline",
        description: "Choose when your runway begins and when the project ends.",
        done: hasStartDate,
        icon: CalendarClock,
        action: onSetTimeline,
        actionLabel: "Set dates",
      },
      {
        id: "funding",
        title: "Add initial funding",
        description: "Record cash on hand so the burn chart starts from reality.",
        done: hasInitialFunding,
        icon: Wallet,
        action: onAddFunding,
        actionLabel: "Add funding",
      },
      {
        id: "expenses",
        title: "Add your first expenses",
        description: "Salary, rent, software — anything that reduces cash each month.",
        done: expenseCount > 0,
        icon: TrendingDown,
        action: onAddExpense,
        actionLabel: "Add expense",
      },
    ],
    [
      hasStartDate,
      hasInitialFunding,
      expenseCount,
      onSetTimeline,
      onAddFunding,
      onAddExpense,
    ],
  );

  const completed = steps.filter((step) => step.done).length;
  const isComplete = completed === steps.length;

  if (isComplete) {
    return null;
  }

  return (
    <div className="mb-6 rounded-[20px] border border-brand/20 bg-gradient-to-br from-brand/10 via-surface-elevated to-surface p-5 ring-1 ring-brand/15">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="sh-kicker">Getting started</p>
          <h2 className="mt-1 flex items-center gap-2 text-xl font-black text-white">
            <Rocket className="h-5 w-5 text-brand" />
            {startupName ? `Set up ${startupName}` : "Set up your startup"}
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-white/65">
            Add a few basics and your runway timeline will come to life.{" "}
            {completed}/{steps.length} setup steps complete.
          </p>
        </div>
        <Button type="button" variant="secondary" size="sm" onClick={onOpenSettings}>
          <Settings2 className="mr-1.5 h-4 w-4" />
          Startup settings
        </Button>
      </div>

      <div className="mt-5 grid gap-3 md:grid-cols-3">
        {steps.map((step) => {
          const Icon = step.icon;
          return (
            <div
              key={step.id}
              className={`rounded-[16px] border p-4 ${
                step.done
                  ? "border-brand/25 bg-brand/5"
                  : "border-white/10 bg-surface/40"
              }`}
            >
              <div className="flex items-start gap-3">
                <span
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] ${
                    step.done ? "bg-brand/20 text-brand" : "bg-white/5 text-white/55"
                  }`}
                >
                  <Icon className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-bold text-white">{step.title}</h3>
                  <p className="mt-1 text-xs text-white/55">{step.description}</p>
                  {!step.done ? (
                    <button
                      type="button"
                      onClick={step.action}
                      className="mt-3 text-xs font-semibold text-brand hover:text-brand-muted"
                    >
                      {step.actionLabel} →
                    </button>
                  ) : (
                    <p className="mt-3 text-xs font-semibold text-brand">Done</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
